import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, X } from 'lucide-react';
import Button from '../../components/ui/Button';

import Header from '../../components/ui/Header';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

import RoleBadge from '../../components/ui/RoleBadge';
import RoleHierarchyIndicator from '../../components/ui/RoleHierarchyIndicator';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { roleService } from '../../services/roleService';
import Icon from '../../components/AppIcon';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { profile, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [success, setSuccess] = useState('');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Dynamic role options from database
  const [roleOptions, setRoleOptions] = useState([]);
  const [userRoles, setUserRoles] = useState({});

  // Add this state for sidebar
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Add this function
  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    roleId: '',
    first_name: '',
    last_name: '',
    department: '',
    phone: ''
  });

  useEffect(() => {
    loadUsers();
    loadRoleOptions();
  }, []);

  const loadRoleOptions = async () => {
    try {
      setError('');
      
      console.log('🔍 Loading roles from database...');
      
      // Try roleService first
      const roles = await roleService?.getAvailableRoles();
      
      console.log('📊 Roles fetched from service:', roles);
      
      // Ensure roles is an array and has data
      if (Array.isArray(roles) && roles?.length > 0) {
        const mappedRoles = roles?.map(role => ({
          value: role?.id,
          label: role?.displayName,
          name: role?.name
        }));
        
        console.log('✅ Roles successfully loaded:', mappedRoles?.length);
        setRoleOptions(mappedRoles);
        return;
      }
      
      // Fallback: fetch directly if service returns empty
      console.log('⚠️ Service returned empty, trying direct fetch...');
      const { data, error: fetchError } = await supabase
        ?.from('system_roles')
        ?.select('*')
        ?.order('hierarchy_level', { ascending: true });
      
      console.log('📊 Direct fetch result:', { data, error: fetchError });
      
      if (fetchError) {
        console.error('❌ Direct fetch error:', fetchError);
        throw fetchError;
      }
      
      if (data && data?.length > 0) {
        const mappedRoles = data?.map(role => ({
          value: role?.id,
          label: role?.display_name,
          name: role?.name
        }));
        
        console.log('✅ Roles loaded via direct fetch:', mappedRoles?.length);
        setRoleOptions(mappedRoles);
      } else {
        const errorMsg = 'No roles found in database. Please add system roles first.';
        console.error('❌', errorMsg);
        setError(errorMsg);
        setRoleOptions([]);
      }
    } catch (err) {
      console.error('❌ Error loading role options:', err);
      setError(`Failed to load roles: ${err?.message || 'Unknown error'}`);
      setRoleOptions([]);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Build query based on role
      let query = supabase?.from('user_profiles')?.select(`
          *,
          tenant:tenants(*)
        `)?.order('created_at', { ascending: false });

      // Super Admins see ALL users across ALL tenants
      // Business Admins and below see only their tenant's users
      if (!isSuperAdmin() && profile?.tenant_id) {
        query = query?.eq('tenant_id', profile?.tenant_id);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Fetch roles for each user
      const usersWithRoles = await Promise.all(
        (data || [])?.map(async (user) => {
          const primaryRole = await roleService?.getUserPrimaryRole(user?.id);
          return {
            ...user,
            primaryRole: primaryRole
          };
        })
      );

      setUsers(usersWithRoles);

      // Store user roles for quick access
      const rolesMap = {};
      for (const user of usersWithRoles) {
        if (user?.primaryRole) {
          rolesMap[user?.id] = user?.primaryRole;
        }
      }
      setUserRoles(rolesMap);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e?.preventDefault();
    try {
      setLoading(true);
      setError('');

      // Get current user's tenant ID
      const currentTenantId = profile?.tenant_id;
      if (!currentTenantId) {
        throw new Error('No tenant ID found for current user');
      }

      // Find selected role details
      const selectedRole = roleOptions?.find(r => r?.value === formData?.roleId);
      if (!selectedRole) {
        throw new Error('Please select a valid role');
      }

      // Create auth user with profile data in metadata
      // Trigger automatically creates user_profiles entry
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            first_name: formData?.first_name,
            last_name: formData?.last_name,
            tenant_id: currentTenantId,
            job_title: formData?.department,
            phone: formData?.phone
          }
        }
      });

      if (authError) throw authError;

      // Assign role to new user
      if (authData?.user?.id) {
        await roleService?.assignRoleToUser(authData?.user?.id, formData?.roleId);
      }

      setSuccess('User created successfully! They can now log in with their credentials.');
      
      // Reset form
      setFormData({
        email: '',
        password: '',
        roleId: '',
        first_name: '',
        last_name: '',
        department: '',
        phone: ''
      });
      
      // Close modal and reload users
      setTimeout(() => {
        setShowAddModal(false);
        loadUsers();
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err?.message);
      console.error('Error adding user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Update profile details
      const { error: updateError } = await supabase?.from('user_profiles')?.update({
          first_name: formData?.first_name,
          last_name: formData?.last_name,
          phone: formData?.phone,
          job_title: formData?.department
        })?.eq('id', selectedUser?.id);

      if (updateError) throw updateError;

      // Update role assignment if changed
      if (formData?.roleId) {
        const currentRoles = await roleService?.getUserRoles(selectedUser?.id);
        
        // Remove old role assignments
        for (const roleAssignment of currentRoles) {
          await roleService?.removeRoleFromUser(selectedUser?.id, roleAssignment?.roleId);
        }

        // Assign new role
        await roleService?.assignRoleToUser(selectedUser?.id, formData?.roleId);
      }

      setSuccess('User updated successfully!');
      setShowEditDrawer(false);
      setSelectedUser(null);
      
      // Refresh user list
      setTimeout(() => {
        loadUsers();
        setSuccess('');
      }, 1500);
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error?.message);
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;

    try {
      setError('');
      setSuccess('');

      const { error: updateError } = await supabase?.from('user_profiles')?.update({
          is_active: false
        })?.eq('id', userId);

      if (updateError) throw updateError;

      setSuccess('User deactivated successfully!');
      
      setTimeout(() => {
        loadUsers();
        setSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Error deactivating user:', error);
      setError(error?.message);
    }
  };

  const openEditDrawer = async (user) => {
    setSelectedUser(user);
    
    // Get user's primary role
    const primaryRole = await roleService?.getUserPrimaryRole(user?.id);
    
    setFormData({
      email: user?.email,
      roleId: primaryRole?.id || '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      department: user?.job_title || '',
      phone: user?.phone || ''
    });
    setShowEditDrawer(true);
  };

  const handleEdit = (user) => {
    openEditDrawer(user);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      setError('');
      setSuccess('');

      // Delete user's role assignments first
      const { error: roleError } = await supabase
        ?.from('user_role_assignments')
        ?.delete()
        ?.eq('user_id', userId);

      if (roleError) throw roleError;

      // Delete user profile
      const { error: deleteError } = await supabase
        ?.from('user_profiles')
        ?.delete()
        ?.eq('id', userId);

      if (deleteError) throw deleteError;

      setSuccess('User deleted successfully!');
      
      setTimeout(() => {
        loadUsers();
        setSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError(error?.message || 'Failed to delete user');
    }
  };

  const filteredUsers = users?.filter((user) => {
    const matchesSearch = 
      user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      user?.first_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      user?.last_name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const userRoleName = user?.primaryRole?.name || '';
    const matchesRole = roleFilter === 'all' || userRoleName === roleFilter;
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user?.is_active) ||
      (statusFilter === 'inactive' && !user?.is_active);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleBadgeColor = (roleName) => {
    const colors = {
      'super_admin': 'bg-purple-100 text-purple-800',
      'sub_account_admin': 'bg-blue-100 text-blue-800',
      'business_admin': 'bg-green-100 text-green-800',
      'staff': 'bg-gray-100 text-gray-800',
      'customer': 'bg-orange-100 text-orange-800'
    };
    return colors?.[roleName] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <ComponentIdBadge
        id="pg-user-management-main"
        name="User Management Main Page"
        description="User administration with role assignment and profile management"
      />
      <Header onToggleSidebar={handleToggleSidebar} />
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-500 mt-1">
              {isSuperAdmin() ? 'Manage all users across organizations' : 'Manage users in your organization'}
            </p>
          </div>
          <PermissionGate feature="users" level="write">
            <Button onClick={() => setShowAddModal(true)}>
              <Icon name="UserPlus" size={16} className="mr-2" />
              Add User
            </Button>
          </PermissionGate>
        </div>
        <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              <Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e?.target?.value)}
                className="w-40"
              >
                <option value="all">All Roles</option>
                {roleOptions?.map(role => (
                  <option key={role?.value} value={role?.name}>
                    {role?.label}
                  </option>
                ))}
              </Select>

              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="w-40"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>

              <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add New User
              </Button>
            </div>
          </div>
        </div>
        <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers?.length > 0 ? (
                  filteredUsers?.map((user) => (
                    <tr key={user?.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openEditDrawer(user)}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                            {user?.first_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user?.first_name && user?.last_name 
                                ? `${user?.first_name} ${user?.last_name}`
                                : user?.email}
                            </div>
                            <div className="text-sm text-gray-500">{user?.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-2">
                          {user?.primaryRole ? (
                            <>
                              <RoleBadge
                                roleName={user?.primaryRole?.name}
                                displayName={user?.primaryRole?.displayName}
                                hierarchyLevel={user?.primaryRole?.hierarchyLevel}
                                size="md"
                                variant="badge"
                                showIcon={true}
                                showHierarchy={true}
                              />
                              <RoleHierarchyIndicator
                                level={user?.primaryRole?.hierarchyLevel}
                                size="sm"
                                showLabel={false}
                                showComparison={false}
                              />
                            </>
                          ) : (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              No Role
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user?.is_active 
                            ? 'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
                        }`}>
                          {user?.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user?.last_login_at 
                          ? new Date(user.last_login_at)?.toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <PermissionGate feature="users" level="write">
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-primary hover:text-primary/80 mr-4"
                          >
                            Edit
                          </button>
                        </PermissionGate>
                        <PermissionGate feature="users" level="delete">
                          <button
                            onClick={() => handleDelete(user?.id)}
                            className="text-error hover:text-error/80"
                          >
                            Delete
                          </button>
                        </PermissionGate>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Add User Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Add New User</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 text-red-600">⚠️</div>
                    <div className="flex-1">
                      <p className="text-sm text-red-800 font-medium">Error Loading Roles</p>
                      <p className="text-xs text-red-600 mt-1">{error}</p>
                      <button 
                        onClick={loadRoleOptions}
                        className="text-xs text-red-700 underline mt-2 hover:text-red-900"
                      >
                        Retry Loading Roles
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Success Display */}
              {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">{success}</p>
                </div>
              )}

              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    value={formData?.email}
                    onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <Input
                    type="password"
                    value={formData?.password}
                    onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
                    required
                    minLength={6}
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <Select
                    value={formData?.roleId}
                    onChange={(e) => setFormData({ ...formData, roleId: e?.target?.value })}
                    options={roleOptions}
                    placeholder="Select a role"
                    className="w-full"
                    required
                  />
                  {roleOptions?.length === 0 && !error && (
                    <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1">
                      <span>⏳</span>
                      <span>Loading roles from database...</span>
                    </p>
                  )}
                  {roleOptions?.length === 0 && error && (
                    <div className="text-xs text-red-600 mt-1">
                      <p className="font-medium">⚠️ No roles available</p>
                      <p className="mt-1">Possible causes:</p>
                      <ul className="list-disc list-inside ml-2 mt-1">
                        <li>Database connection issue</li>
                        <li>Missing role permissions</li>
                        <li>No system roles configured</li>
                      </ul>
                    </div>
                  )}
                  {roleOptions?.length > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ {roleOptions?.length} role{roleOptions?.length !== 1 ? 's' : ''} available
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <Input
                      type="text"
                      value={formData?.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e?.target?.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <Input
                      type="text"
                      value={formData?.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e?.target?.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                  <Input
                    type="text"
                    value={formData?.department}
                    onChange={(e) => setFormData({ ...formData, department: e?.target?.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <Input
                    type="tel"
                    value={formData?.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1" disabled={loading}>
                    {loading ? 'Creating...' : 'Create User'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Edit User Drawer */}
        {showEditDrawer && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50">
            <div className="bg-white w-full max-w-md h-full overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">User Details</h2>
                  <button onClick={() => setShowEditDrawer(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleEditUser} className="space-y-6">
                  {/* User Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center mb-4">
                      <div className="h-16 w-16 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-xl">
                        {selectedUser?.first_name?.[0]?.toUpperCase() || selectedUser?.email?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {selectedUser?.first_name && selectedUser?.last_name 
                            ? `${selectedUser?.first_name} ${selectedUser?.last_name}`
                            : selectedUser?.email}
                        </h3>
                        <p className="text-sm text-gray-500">{selectedUser?.email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <Input
                        type="text"
                        value={formData?.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e?.target?.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <Input
                        type="text"
                        value={formData?.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e?.target?.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <Input
                      type="email"
                      value={formData?.email}
                      onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <Input
                      type="tel"
                      value={formData?.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                    <Input
                      type="text"
                      value={formData?.department}
                      onChange={(e) => setFormData({ ...formData, department: e?.target?.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                    <Select
                      value={formData?.roleId}
                      onChange={(e) => setFormData({ ...formData, roleId: e?.target?.value })}
                      options={roleOptions}
                      placeholder="Select a role"
                      className="w-full"
                      required
                    />
                    {roleOptions?.length === 0 && !error && (
                      <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1">
                        <span>⏳</span>
                        <span>Loading roles from database...</span>
                      </p>
                    )}
                    {roleOptions?.length === 0 && error && (
                      <div className="text-xs text-red-600 mt-1">
                        <p className="font-medium">⚠️ No roles available</p>
                        <p className="mt-1">Possible causes:</p>
                        <ul className="list-disc list-inside ml-2 mt-1">
                          <li>Database connection issue</li>
                          <li>Missing role permissions</li>
                          <li>No system roles configured</li>
                        </ul>
                      </div>
                    )}
                    {roleOptions?.length > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ {roleOptions?.length} role{roleOptions?.length !== 1 ? 's' : ''} available
                      </p>
                    )}
                  </div>

                  {/* Activity Log */}
                  <div className="border-t pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Activity Log</h3>
                    <div className="space-y-2">
                      <div className="text-sm">
                        <span className="text-gray-600">Created:</span>
                        <span className="ml-2 text-gray-900">{new Date(selectedUser.created_at)?.toLocaleString()}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600">Last Updated:</span>
                        <span className="ml-2 text-gray-900">{new Date(selectedUser.updated_at)?.toLocaleString()}</span>
                      </div>
                      {selectedUser?.last_login_at && (
                        <div className="text-sm">
                          <span className="text-gray-600">Last Login:</span>
                          <span className="ml-2 text-gray-900">{new Date(selectedUser.last_login_at)?.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1">
                      Save Changes
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setShowEditDrawer(false)}
                      className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}